import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Search, X } from "lucide-react";
import {
    employeesApi,
    residentsApi,
    hostelCompaniesApi,
    shopProductsApi,
    candidatesApi,
    leadgenApi,
} from "@/lib/api";

const SEARCH_SOURCES = [
    { key: "employees", label: "Сотрудники", route: "/employees", list: () => employeesApi.list() },
    { key: "residents", label: "Жильцы", route: "/hostel/residents", list: (query) => residentsApi.list(query) },
    { key: "companies", label: "Компании", route: "/hostel/companies", list: () => hostelCompaniesApi.list() },
    { key: "products", label: "Товары", route: "/shop/products", list: () => shopProductsApi.list() },
    { key: "candidates", label: "Кандидаты", route: "/candidates", list: () => candidatesApi.list() },
    { key: "leads", label: "Лиды", route: "/leadgen/leads", list: () => leadgenApi.leads() },
];

function asArray(data) {
    if (Array.isArray(data)) return data;
    return data?.items || data?.results || data?.data || [];
}

function displayValue(item) {
    return item.name || item.full_name || item.title || item.product_name || item.company_name || item.phone || item.email || item.id;
}

function matches(item, query) {
    return Object.values(item || {})
        .filter((value) => typeof value === "string" || typeof value === "number")
        .join(" ")
        .toLowerCase()
        .includes(query);
}

export default function GlobalSearch({ open, onClose }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setQuery("");
            setResults([]);
            return undefined;
        }
        const normalized = query.trim().toLowerCase();
        if (normalized.length < 2) {
            setResults([]);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const timer = window.setTimeout(async () => {
            const settled = await Promise.allSettled(
                SEARCH_SOURCES.map((source) => source.list(source.key === "residents" ? normalized : undefined)),
            );
            if (cancelled) return;
            const next = [];
            settled.forEach((entry, index) => {
                if (entry.status !== "fulfilled") return;
                asArray(entry.value.data)
                    .filter((item) => matches(item, normalized))
                    .slice(0, 8)
                    .forEach((item) => next.push({
                        id: `${SEARCH_SOURCES[index].key}-${item.id || item.key || item.external_id}`,
                        title: displayValue(item),
                        subtitle: item.phone || item.email || item.status || item.article || SEARCH_SOURCES[index].label,
                        label: SEARCH_SOURCES[index].label,
                        route: SEARCH_SOURCES[index].route,
                    }));
            });
            setResults(next.slice(0, 30));
            setLoading(false);
        }, 250);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [open, query]);

    const grouped = useMemo(() => results.reduce((groups, result) => {
        (groups[result.label] ||= []).push(result);
        return groups;
    }, {}), [results]);

    if (!open) return null;

    const openResult = (result) => {
        onClose();
        navigate(result.route);
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 sm:p-8" role="dialog" aria-modal="true" aria-label="Глобальный поиск">
            <button type="button" className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onClose} aria-label="Закрыть поиск" />
            <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", boxShadow: "var(--shadow-lg)" }}>
                <div className="flex items-center gap-3 px-4 border-b" style={{ borderColor: "var(--glass-border)" }}>
                    <Search className="w-5 h-5 shrink-0" style={{ color: "var(--accent)" }} />
                    <input
                        autoFocus
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Найти сотрудника, жильца, компанию, товар..."
                        className="h-14 flex-1 bg-transparent outline-none text-sm"
                        style={{ color: "var(--foreground)" }}
                    />
                    {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--foreground-3)" }} />}
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg" aria-label="Закрыть">
                        <X className="w-4 h-4" style={{ color: "var(--foreground-3)" }} />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-2">
                    {query.trim().length < 2 && (
                        <p className="px-3 py-8 text-center text-sm" style={{ color: "var(--foreground-3)" }}>
                            Введите минимум 2 символа
                        </p>
                    )}
                    {query.trim().length >= 2 && !loading && results.length === 0 && (
                        <p className="px-3 py-8 text-center text-sm" style={{ color: "var(--foreground-3)" }}>
                            Ничего не найдено
                        </p>
                    )}
                    {Object.entries(grouped).map(([label, items]) => (
                        <div key={label} className="mb-2">
                            <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--foreground-3)" }}>{label}</div>
                            {items.map((result) => (
                                <button
                                    type="button"
                                    key={result.id}
                                    onClick={() => openResult(result)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-[var(--glass-bg-hover)]"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold truncate" style={{ color: "var(--foreground)" }}>{result.title}</div>
                                        <div className="text-xs truncate" style={{ color: "var(--foreground-3)" }}>{result.subtitle}</div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 shrink-0" style={{ color: "var(--foreground-3)" }} />
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
